
import Logger from '@/logger'
import AppError from '@/models/app-error'
import ServiceBasketItem from '@/models/service-basket-item'
import ServiceFactory from '@/services/service-factory'
import Store from '@/store'
import { MUTATIONS } from '@/store/mutations'

const LOGGING_CATEGORY = 'Service Basket Manager'

const ServiceBasketManager = {
    /**
     * Returns the current address id.
     * @returns {number}
     * @private
     */
    get _addressId() {
        if (!Store.state.property)
            throw new Error('Property not found.')

        return Store.state.property.id
    },

    /**
     * Returns the current renovation plan id.
     * @returns {number}
     * @private
     */
    get _renovationId() {
        if (!Store.state.improvements.currentProject.id)
            throw new Error('Current project has no id.')

        return Store.state.improvements.currentProject.id
    },

    /**
     * Load the service basket.
     */
    async loadBasketAsync() {
        Logger.trackMethod('loadBasketAsync', 'ServiceBasketManager')
        let operationState

        try {
            const service = ServiceFactory.basket()
            operationState = await service.loadServiceBasketAsync(this._addressId)

            if (!operationState.succeeded)
                operationState.throw()

            Store.commit(MUTATIONS.SERVICE_BASKET.setBasket, operationState.data)
            Logger.logObject({ ...Store.state.serviceBasket.basket }, 'Retrieving service basket', LOGGING_CATEGORY)
        } catch (err) {
            Logger.logObject(operationState, `Unable to load service basket: ${ err.message }`, LOGGING_CATEGORY)
            Store.commit(MUTATIONS.logAppError, new AppError('Unable to load your previously saved basket', false))
        }
    },

    /**
     * Create a new ServiceBasketItem from the provided ServiceProduct and add it
     * to the basket.
     * @param {ServiceProduct} item
     */
    async addItemToBasketAsync(item) {
        Logger.trackMethod('addItemToBasket', 'ServiceBasketManager', item)

        if (Store.state.serviceBasket.basket.hasItem(item.serviceTypeId)) {
            Logger.log(`Failed to add product to basket. Product ${ item.serviceTypeId } already exists.`, LOGGING_CATEGORY)
            return
        }

        let operationState

        try {
            const serviceBasketItem = ServiceBasketItem.ensure({ ...item, addressId: this._addressId })
            const service = ServiceFactory.basket()

            operationState = await service.upsertServiceBasketItemAsync(serviceBasketItem, this._renovationId)

            if (!operationState.succeeded)
                operationState.throw()

            Store.commit(MUTATIONS.SERVICE_BASKET.addItemToBasket, serviceBasketItem)
            Logger.logObject({ ...Store.state.serviceBasket.basket }, 'Added item to service basket', LOGGING_CATEGORY)
        } catch (err) {
            Logger.logObject(operationState, `Unable to add service basket item: ${ err.message }`, LOGGING_CATEGORY)
            Store.commit(MUTATIONS.logAppError, new AppError('Unable to add service basket item.', false))
        }
    },

    /**
     * Remove an ServiceBasketItem from the basket by the serviceTypeId of the provided ServiceProduct.
     * @param {ServiceProduct} item
     */
    async removeItemFromBasketAsync(item) {
        Logger.trackMethod('removeItemFromBasket', 'ServiceBasketManager', item)

        if (!Store.state.serviceBasket.basket.hasItem(item.serviceTypeId)) {
            Logger.log(`Failed to remove product from basket. Product ${ item.serviceTypeId } doesn't exist.`, LOGGING_CATEGORY)
            return
        }

        let operationState

        try {
            const service = ServiceFactory.basket()

            operationState = await service.deleteServiceBasketItemAsync(this._addressId, item.serviceTypeId)

            if (!operationState.succeeded)
                operationState.throw()

            Store.commit(MUTATIONS.SERVICE_BASKET.removeItemFromBasket, item)
            Logger.logObject({ ...Store.state.serviceBasket.basket }, 'Removed item from service basket', LOGGING_CATEGORY)
        } catch (err) {
            Logger.logObject(operationState, `Unable to remove service basket item: ${ err.message }`, LOGGING_CATEGORY)
            Store.commit(MUTATIONS.logAppError, new AppError('Unable to remove service basket item.', false))
        }
    },
}

export default ServiceBasketManager
